import {useState, useEffect} from 'react'
import useBlacklist from '../../hooks/use-blacklist'
import useSetting from '../../hooks/use-setting'
import styles from './blacklist-settings.module.css'

const BlacklistSettings = () => {
  const {blacklist, refreshBlacklist} = useBlacklist()
  const [enableBlacklist, setEnableBlacklist] = useSetting('enableBlacklist', true)
  const [showBlacklistWarnings, setShowBlacklistWarnings] = useSetting('showBlacklistWarnings', true)
  const [refreshing, setRefreshing] = useState(false)

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      await refreshBlacklist()
    } finally {
      setRefreshing(false)
    }
  }

  const formatLastUpdated = (timestamp) => {
    if (!timestamp) return 'Never'
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / (1000 * 60))

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} minutes ago`
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hours ago`
    return date.toLocaleString()
  }

  return (
    <div className={styles.blacklistSettings}>
      <h3>Content Moderation & Blacklist</h3>

      <div className={styles.section}>
        <h4>Settings</h4>

        <div className={styles.setting}>
          <label>
            <input type="checkbox" checked={enableBlacklist} onChange={(e) => setEnableBlacklist(e.target.checked)} />
            Enable content filtering (recommended)
          </label>
          <p className={styles.description}>Hide posts, comments, and subplebbits that have been flagged by content moderation.</p>
        </div>

        <div className={styles.setting}>
          <label>
            <input type="checkbox" checked={showBlacklistWarnings} onChange={(e) => setShowBlacklistWarnings(e.target.checked)} disabled={!enableBlacklist} />
            Show warnings for flagged content
          </label>
          <p className={styles.description}>Display warning labels on content from flagged authors or subplebbits.</p>
        </div>
      </div>

      <div className={styles.section}>
        <h4>Blacklist Status</h4>

        <div className={styles.status}>
          <div className={styles.statusItem}>
            <strong>Last Updated:</strong> {formatLastUpdated(blacklist.lastUpdated)}
          </div>

          {blacklist.error && (
            <div className={styles.error}>
              <strong>Error:</strong> {blacklist.error}
            </div>
          )}

          <div className={styles.stats}>
            <div className={styles.statItem}>
              <span className={styles.statNumber}>{blacklist.subplebbits?.size || 0}</span>
              <span className={styles.statLabel}>Blacklisted Subplebbits</span>
            </div>

            <div className={styles.statItem}>
              <span className={styles.statNumber}>{blacklist.authors?.size || 0}</span>
              <span className={styles.statLabel}>Blacklisted Authors</span>
            </div>

            <div className={styles.statItem}>
              <span className={styles.statNumber}>{blacklist.comments?.size || 0}</span>
              <span className={styles.statLabel}>Flagged Comments</span>
            </div>
          </div>

          <button className={styles.refreshButton} onClick={handleRefresh} disabled={refreshing || blacklist.isLoading}>
            {refreshing || blacklist.isLoading ? 'Refreshing...' : 'Refresh Blacklist'}
          </button>
        </div>
      </div>

      <div className={styles.section}>
        <h4>How It Works</h4>
        <div className={styles.info}>
          <p>Plebones integrates with plebbit-indexer's content moderation system to filter out inappropriate content. This includes:</p>
          <ul>
            <li>
              <strong>Subplebbit Filtering:</strong> Hides entire subplebbits that violate community guidelines
            </li>
            <li>
              <strong>Author Filtering:</strong> Hides all content from authors who have been flagged
            </li>
            <li>
              <strong>Comment Filtering:</strong> Hides individual posts/comments that have been reported
            </li>
          </ul>
          <p>
            The blacklist is updated automatically every 5 minutes from the moderation API at{' '}
            <code>{process.env.REACT_APP_BLACKLIST_API_URL || 'https://plebscan.org'}</code>
          </p>
        </div>
      </div>
    </div>
  )
}

export default BlacklistSettings
